import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { signOut } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Select,
  Avatar,
  Menu,
  MenuItem,
  Separator,
  SideNav,
  PortalAppBar,
  Icon,
} from '@glplus/glpweb-dsl';
import { useVendor } from '../lib/CapabilitiesContext';
import { useLocationScope } from '../lib/LocationScopeContext';
import { vendorApiClient } from '../lib/vendorApiClient';
import { onProductsChanged } from '../lib/productEvents';
import { LANGUAGE_SENT_KEY, currentLanguage, switchPortalLanguage } from '../lib/portalLanguage';
import MainFooter from './MainFooter';

/**
 * The vendor portal shell on the §15z IA (ruled 2026-09-13): the SIDE NAV
 * holds every section; the TOP BAR is logo · vendor switcher (more than one
 * membership) · location switcher · avatar + logged-in user. Built from the
 * DSL `PortalAppBar` + `SideNav` with the vendor colour overrides the §15z
 * build note names (active text orangeInk, background highlightOrange, rail
 * brand orange). Chrome colours come from `portalChrome.vendor` — the §15cc
 * values (#F67A1A → #DF6514, 19px bold labels) are owed in the DSL and land
 * here the moment that token change ships.
 *
 * Members get the side nav; applicants (no membership yet) get header + content.
 */

const SIDE_NAV_WIDTH = 240;
const ORANGE_INK = '#A85200';
const HIGHLIGHT_ORANGE = '#FEF1E7';
const BRAND_ORANGE = '#F67A1A';
const INACTIVE = '#343434';
const LOGIN_URL = '/login';

interface NavDef {
  navId: string;
  route: string;
  icon: string;
  dividerAbove?: boolean;
  capability?: string;
}

// §15z, in order. Reports & analytics and Food recalls were added by ruling;
// Business absorbs profile / team / support contacts / certifications (§15u).
const NAV: NavDef[] = [
  { navId: 'dashboard', route: '/', icon: 'overview' },
  { navId: 'products', route: '/products', icon: 'listAlt', capability: 'catalog:view' },
  { navId: 'locations', route: '/locations', icon: 'location', capability: 'location:view' },
  { navId: 'promotions', route: '/promotions', icon: 'loyalty', capability: 'promotion:manage' },
  { navId: 'orders', route: '/orders', icon: 'onlineordering', capability: 'order:view' },
  { navId: 'payouts', route: '/payouts', icon: 'attachMoney', capability: 'payout:view' },
  { navId: 'reports', route: '/reports', icon: 'trendingUp' },
  { navId: 'recalls', route: '/recalls', icon: 'foodrecalls', capability: 'recall:view' },
  { navId: 'business', route: '/business', icon: 'storefront', dividerAbove: true, capability: 'profile:edit' },
  { navId: 'help', route: '/help', icon: 'help' },
];

export default function VendorShell({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data, activeVendorId, setActiveVendorId, activeMembership, has } = useVendor();
  const memberships = data?.memberships ?? [];
  const hasMembership = memberships.length > 0;

  // The Products badge counts what is waiting on the vendor. Re-read when they
  // move between sections, since answering is what clears it.
  const section = pathname.split('/')[1] ?? '';
  const [needsAnswer, setNeedsAnswer] = useState(0);
  useEffect(() => {
    if (!hasMembership || !activeVendorId) return;
    let alive = true;
    const refresh = () =>
      vendorApiClient
        .getProductsSummary(activeVendorId)
        .then((s) => alive && setNeedsAnswer(s.needsAnswer))
        .catch(() => undefined);
    void refresh();
    // A page that answers or raises a question says so; the count is not this component's to track.
    const off = onProductsChanged(() => void refresh());
    return () => {
      alive = false;
      off();
    };
  }, [hasMembership, activeVendorId, section]);

  // Emails to this person are written in the language they run the portal in.
  const language = currentLanguage(i18n);
  useEffect(() => {
    if (!data) return;
    const key = LANGUAGE_SENT_KEY;
    try {
      if (window.sessionStorage.getItem(key) === language) return;
      window.sessionStorage.setItem(key, language);
    } catch {
      // No session storage: send it anyway.
    }
    vendorApiClient.setLanguage(language).catch(() => undefined);
  }, [data, language]);

  const items = NAV.filter((n) => !n.capability || has(n.capability)).map((n) => ({
    navId: n.navId,
    route: n.route,
    href: n.route,
    defaultLabel: t(`nav.${n.navId}`),
    languageKey: `nav.${n.navId}`,
    ariaLabel: t(`nav.${n.navId}`),
    icon: n.icon,
    children: [],
    mobileOnly: false,
    exact: n.route === '/',
    dividerAbove: n.dividerAbove ?? false,
  }));

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F5F6F8' }}>
      <PortalAppBar
        portal="vendor"
        onLogoClick={() => navigate('/')}
        right={<TopBarRight hasMembership={hasMembership} />}
        dataTestId="vendor-app-bar"
      />

      <Box sx={{ flex: 1, display: 'flex', alignItems: 'stretch' }}>
        {hasMembership && (
          <Box
            component="aside"
            sx={{ width: SIDE_NAV_WIDTH, flexShrink: 0, bgcolor: '#fff', borderRight: '1px solid #E5E5E5', display: { xs: 'none', md: 'block' } }}
          >
            <SideNav
              items={items}
              currentPath={pathname}
              linkComponent={Link}
              linkHrefProp="to"
              variant="permanent"
              width={SIDE_NAV_WIDTH}
              ariaLabel={t('nav.mainNavigation')}
              activeColor={ORANGE_INK}
              activeBackgroundColor={HIGHLIGHT_ORANGE}
              activeRailColor={BRAND_ORANGE}
              inactiveColor={INACTIVE}
              hideMobileOnly
              renderItemTrailing={(item) =>
                item.id === 'products' && needsAnswer > 0 ? (
                  <Box
                    component="span"
                    aria-label={t('nav.productsNeedAnswer', { count: needsAnswer })}
                    data-testid="nav-products-badge"
                    sx={{ px: 0.75, minWidth: 22, textAlign: 'center', borderRadius: '11px', border: `1.5px solid ${ORANGE_INK}`, color: ORANGE_INK, fontSize: 12, fontWeight: 700, lineHeight: '18px' }}
                  >
                    {needsAnswer}
                  </Box>
                ) : null
              }
            />
          </Box>
        )}
        <Box
          component="main"
          id="main-content"
          sx={{ flex: 1, minWidth: 0, px: { xs: '16px', sm: '32px', lg: '48px' }, py: { xs: 3, md: 4 } }}
        >
          {children}
        </Box>
      </Box>

      <MainFooter />
    </Box>
  );

  // Rendered inside PortalAppBar's right slot — a closure so it shares the
  // shell's memberships / router without prop threading.
  function TopBarRight({ hasMembership: member }: { hasMembership: boolean }) {
    const email = data?.email ?? '';
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

    const onSignOut = async () => {
      setMenuAnchor(null);
      try {
        await signOut();
      } finally {
        window.location.assign(LOGIN_URL);
      }
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, ml: 'auto' }}>
        {memberships.length > 1 && (
          <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 160 }}>
            <Select
              ariaLabel={t('nav.activeVendor')}
              value={activeVendorId ?? ''}
              onChange={(_e, value) => setActiveVendorId(value)}
              options={memberships.map((m) => ({ value: m.vendorId, label: m.businessName }))}
              size="small"
              sx={{ bgcolor: '#fff', borderRadius: '8px' }}
            />
          </Box>
        )}
        {member && activeVendorId && <LocationSwitcher vendorId={activeVendorId} />}

        <Box
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          role="button"
          aria-haspopup="menu"
          aria-expanded={!!menuAnchor}
          aria-label={t('nav.accountMenu')}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setMenuAnchor(e.currentTarget);
            }
          }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            borderRadius: 1,
            px: 0.5,
            py: 0.5,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.12)' },
            '&:focus-visible': { outline: '2px solid #fff', outlineOffset: 2 },
          }}
        >
          {/* §15cc: nothing under 19px bold sits on the orange. */}
          <Typography sx={{ display: { xs: 'none', md: 'block' }, color: '#fff', fontWeight: 700, fontSize: '19px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {data?.memberships.length ? email.split('@')[0] : email}
          </Typography>
          {/* §15z: the live Avatar painted #fff on #fff was invisible — orangeInk initials on white. */}
          <Avatar ariaLabel={t('nav.accountMenu')} fallbackText={email || undefined} bgColor="#ffffff" size="small" />
          <Icon name="expandMore" size={20} color="#ffffff" />
        </Box>

        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { minWidth: 240 } } }}
        >
          <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1.5, alignItems: 'center' }}>
            <Avatar ariaLabel={email} fallbackText={email || undefined} bgColor="#94a3a0" size="medium" />
            <Box sx={{ minWidth: 0 }}>
              {activeMembership && (
                <Typography sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {activeMembership.businessName}
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: '#6b7a73', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>
                {email}
              </Typography>
            </Box>
          </Box>
          <Separator />
          <Box sx={{ px: 2, py: 1.5 }} data-testid="account-language">
            <Select
              ariaLabel={t('nav.language')}
              label={t('nav.language')}
              value={language}
              onChange={(_e, value) => switchPortalLanguage(i18n, value, true)}
              options={[
                { value: 'en', label: 'English' },
                { value: 'es', label: 'Español' },
              ]}
              size="small"
              fullWidth
            />
          </Box>
          <Separator />
          <MenuItem icon="logout" onClick={() => { void onSignOut(); }}>
            {t('nav.signOut')}
          </MenuItem>
        </Menu>
      </Box>
    );
  }
}

/** "All locations (n)" — §15z top bar, scope recorded for later phases (V16). */
function LocationSwitcher({ vendorId }: { vendorId: string }) {
  const { t } = useTranslation();
  const { scope, setScope } = useLocationScope();
  const [locations, setLocations] = useState<Array<{ storeId: string; name: string }>>([]);

  useEffect(() => {
    let alive = true;
    vendorApiClient
      .listLocations(vendorId)
      .then((r) => alive && setLocations(r.items.filter((l) => l.status === 'live').map((l) => ({ storeId: l.storeId, name: l.name }))))
      .catch(() => alive && setLocations([]));
    return () => {
      alive = false;
    };
  }, [vendorId]);

  const options = [
    { value: 'all', label: t('nav.allLocations', { count: locations.length }) },
    ...locations.map((l) => ({ value: l.storeId, label: l.name })),
  ];
  return (
    <Box sx={{ display: { xs: 'none', md: 'block' }, minWidth: 200 }}>
      <Select
        ariaLabel={t('nav.locationScope')}
        value={locations.some((l) => l.storeId === scope) ? scope : 'all'}
        onChange={(_e, v) => setScope(v || 'all')}
        options={options}
        size="small"
        sx={{ bgcolor: '#fff', borderRadius: '8px', '& .MuiSelect-select': { fontWeight: 700, fontSize: '19px', py: '6px' } }}
      />
    </Box>
  );
}
